/**
 * TrendSection — Repeatable section wrapper
 *
 * DESIGN (mirrors imagio SectionContainer):
 *   • Alternating backgrounds: even → white, odd → light grey
 *   • Section heading with gradient accent hairline
 *   • Lazy-loads content via IntersectionObserver sentinel
 */

import type { ReactNode } from "react";
import { useEffect, useRef, useState } from "react";
import "./TrendSection.css";

interface TrendSectionProps {
  title: string;
  /** Small label shown next to the title (e.g. "Inspired by nature"). */
  subtitle?: string;
  index: number;
  children: ReactNode;
}

export const TrendSection = ({
  title,
  subtitle,
  index,
  children,
}: TrendSectionProps) => {
  const [loaded, setLoaded] = useState(index < 2); // first 2 sections load immediately
  const sentinelRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (loaded) return;
    const el = sentinelRef.current;
    if (!el) return;
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setLoaded(true);
          observer.disconnect();
        }
      },
      { threshold: 0.04 },
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, [loaded]);

  const isEven = index % 2 === 0;

  return (
    <section
      className={`trend-section${isEven ? " trend-section--even" : " trend-section--odd"}`}
      id={`trend-section-${index}`}
    >
      {/* Lazy-load sentinel */}
      {!loaded && (
        <div
          ref={sentinelRef}
          className="trend-section__sentinel"
          aria-hidden="true"
        />
      )}

      <div className="trend-section__header">
        <div
          className={`trend-section__accent${isEven ? " trend-section__accent--primary" : " trend-section__accent--secondary"}`}
          aria-hidden="true"
        />
        <div className="trend-section__heading-row">
          <h2 className="trend-section__title">{title}</h2>
          {subtitle && (
            <span className="trend-section__subtitle">{subtitle}</span>
          )}
        </div>
      </div>

      {loaded && <div className="trend-section__content">{children}</div>}
    </section>
  );
};
